import type { DensityDetail } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент деталей плотности §7.6. Маршрут — `GET /api/density/{id}`
 * запросного канала демона; тип ответа приходит из `@screener/protocol`,
 * а коды отказов — транспортные, как у `auth-api.ts`: сессия выражается
 * кодом HTTP, а не сообщением контракта.
 */

const DENSITY_BASE = "/api/density";

/**
 * Три отказа, не один. «Нет сессии» (`401`) отправляет человека на форму
 * входа, «нет такой плотности» (`404`) означает, что стена уже ушла и панель
 * открывать незачем, всё остальное — недоступность демона, при которой
 * повторить запрос осмысленно. Смешение первых двух выбрасывало бы вошедшего
 * на форму при каждой съеденной стене (та же причина, что у
 * `CurrentUserResult`).
 */
export type DensityDetailResult =
  | { status: "ok"; detail: DensityDetail }
  | { status: "unauthorized" }
  | { status: "not-found" }
  | { status: "unavailable" };

/**
 * История §7.6: `read` обязана нести массив — панель раскладывает его через
 * `history.records.map`, и без проверки формы чужое тело уронило бы
 * весь рендер `TypeError`, а не одну панель.
 */
function isValidHistory(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }
  if (value.kind === "read") {
    // Запись библиотеки раскладывается через `[...record.approaches]`.
    return (
      Array.isArray(value.records) &&
      value.records.every((record) => isRecord(record) && Array.isArray(record.approaches))
    );
  }
  return value.kind === "unavailable";
}

/** Контекст §7.6: панель ветвится по `kind`, и третьего вида быть не должно. */
function isValidContext(value: unknown): boolean {
  return isRecord(value) && (value.kind === "measured" || value.kind === "book_not_synced");
}

/** Идущий подход: `null` либо объект с величинами — панель читает `metrics`. */
function isValidOngoingApproach(value: unknown): boolean {
  return value === null || (isRecord(value) && isRecord(value.metrics));
}

function isDensityDetail(value: unknown): value is DensityDetail {
  // Проверяются ровно те поля, по которым панель потом ходит, а не каждое
  // поле §6.2 целиком: форму полей держит контракт, а здесь ловится чужое
  // тело — `index.html` на рассинхроне статики и демона разбирается как JSON
  // не всегда, но ответ прежней версии демона разобраться может.
  return (
    isRecord(value) &&
    isRecord(value.density) &&
    isValidContext(value.context) &&
    isValidHistory(value.history) &&
    "ongoing_approach" in value &&
    isValidOngoingApproach(value.ongoing_approach)
  );
}

/**
 * Детали одной плотности по её `density_id`. Идентификатор кладётся в путь
 * через `encodeURIComponent`: имя детерминировано демоном, но оно строка,
 * и собирать путь конкатенацией значит верить в её форму.
 */
export async function fetchDensityDetail(densityId: string): Promise<DensityDetailResult> {
  let response: Response;
  try {
    response = await fetch(`${DENSITY_BASE}/${encodeURIComponent(densityId)}`, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not-found" };
  }
  if (!response.ok) {
    console.error("детали плотности отклонены демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isDensityDetail(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", detail: body };
  } catch {
    return { status: "unavailable" };
  }
}
